import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, ArrowLeft, ArrowRight, MessageCircle, Phone, MapPin } from 'lucide-react';
import { Helmet } from 'react-helmet';
import SEO from '../components/SEO';
import { SITE } from '../lib/site';
import { getBlogPost, getRelatedPosts } from '../data/blogData';
import { BUSINESS_INFO, REVIEW_SOURCES } from '../data/reviewSources';

// Generate Article Schema
const getArticleSchema = (post, slug) => ({
  '@context': 'https://schema.org',
  '@type': 'BlogPosting',
  headline: post.title,
  description: post.seoDescription || post.metaDescription,
  image: post.featuredImage && !post.featuredImage.startsWith('http') ? SITE + post.featuredImage : post.featuredImage,
  datePublished: post.date,
  dateModified: post.updated || post.date,
  inLanguage: 'en-IN',
  // Set post.author ({ name, url }) once a named author is confirmed.
  author: post.author ? { '@type': 'Person', ...post.author } : { '@id': `${SITE}/#store` },
  publisher: { '@id': `${SITE}/#store` },
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': `${SITE}/blog/${slug}/`
  }
});

// Generate Breadcrumb Schema
const getBreadcrumbSchema = (post, slug) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: SITE + '/'
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Blog',
      item: SITE + '/blog/'
    },
    {
      '@type': 'ListItem',
      position: 3,
      name: post.title,
      item: `${SITE}/blog/${slug}/`
    }
  ]
});

const BlogArticle = () => {
  const { slug } = useParams();
  const post = getBlogPost(slug);

  const handleWhatsApp = () => {
    const message = 'Hello, I want to know about available puppies at Petsmart Puppies.';
    window.open(`https://wa.me/917217648679?text=${encodeURIComponent(message)}`, '_blank');
  };

  if (!post) {
    return <Navigate to="/blog/" replace />;
  }

  const relatedPosts = getRelatedPosts(post.relatedPosts || []);
  // Titles over ~60 characters are cut off in search results; use the short SEO title when there is one.
  const pageTitle = post.seoTitle || `${post.title} | Petsmart Puppies`;

  return (
    <>
      <SEO path={`/blog/${slug}`} title={pageTitle} description={post.seoDescription || post.metaDescription} image={post.featuredImage} type="article" breadcrumbs={false} />
      <Helmet>
        <meta property="article:published_time" content={post.date} />
        <meta property="article:modified_time" content={post.updated || post.date} />
        <script type="application/ld+json">
          {JSON.stringify(getArticleSchema(post, slug))}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(getBreadcrumbSchema(post, slug))}
        </script>
      </Helmet>

      <article data-testid="blog-article-page" style={{ backgroundColor: 'var(--neutral-bg)', minHeight: '100vh' }}>
        {/* Hero Section */}
        <section
          className="py-8 sm:py-12"
          style={{
            background: 'linear-gradient(to bottom right, #E0F2FE, #FFFFFF)',
            borderBottom: '1px solid var(--neutral-border)'
          }}
        >
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              {/* Breadcrumb */}
              <Link 
                to="/blog/" 
                className="inline-flex items-center gap-2 text-sm font-medium mb-6 transition-colors hover:text-[var(--primary-main)]"
                style={{ color: 'var(--text-muted)' }}
              >
                <ArrowLeft size={16} /> Back to Blog
              </Link>

              {/* Category & Meta */}
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span 
                  className="px-3 py-1 rounded-full text-xs font-semibold"
                  style={{ backgroundColor: 'var(--primary-main)', color: 'white' }}
                >
                  {post.category}
                </span>
                <span className="text-sm" style={{ color: 'var(--text-muted)' }}>
                  {new Date(post.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                </span>
                <span className="flex items-center gap-1 text-sm" style={{ color: 'var(--text-muted)' }}>
                  <Clock size={14} />
                  {post.readTime}
                </span>
              </div>

              {/* Title */}
              <h1 
                className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-6"
                style={{ color: 'var(--text-main)', lineHeight: '1.2' }}
              >
                {post.title}
              </h1>

              {/* Featured Image */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="rounded-xl sm:rounded-2xl overflow-hidden shadow-soft"
              >
                <img
                  src={post.featuredImage}
                  alt={post.title}
                  className="w-full object-cover"
                  style={{ maxHeight: '400px' }}
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-8 sm:py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="grid lg:grid-cols-3 gap-8">
                {/* Main Content */}
                <div className="lg:col-span-2">
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className="prose prose-lg max-w-none"
                    style={{
                      backgroundColor: 'white',
                      padding: '1.5rem',
                      borderRadius: '1rem',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}
                  >
                    <div 
                      className="blog-content"
                      dangerouslySetInnerHTML={{ __html: post.content }}
                      style={{ color: 'var(--text-main)' }}
                    />
                  </motion.div>

                  {/* WhatsApp CTA */}
                  <div 
                    className="mt-8 p-6 rounded-xl text-center"
                    style={{ backgroundColor: '#25D366', color: 'white' }}
                  >
                    <h3 className="text-xl font-bold mb-2">Have Questions?</h3>
                    <p className="mb-4 opacity-90">Chat with us on WhatsApp for instant answers</p>
                    <button
                      onClick={handleWhatsApp}
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold transition-all hover:scale-105"
                      style={{ backgroundColor: 'white', color: '#25D366', border: 'none', cursor: 'pointer' }}
                    >
                      <MessageCircle size={20} /> Chat Now
                    </button>
                  </div>
                </div>

                {/* Sidebar */}
                <aside className="lg:col-span-1">
                  {/* Store Info Card */}
                  <div 
                    className="p-5 rounded-xl mb-6 sticky top-24"
                    style={{ backgroundColor: 'white', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                  >
                    <h3 className="font-bold mb-4" style={{ color: 'var(--text-main)' }}>
                      Visit Our Store
                    </h3>
                    
                    <div className="space-y-3 text-sm mb-4">
                      <div className="flex items-start gap-2" style={{ color: 'var(--text-muted)' }}>
                        <MapPin size={16} className="mt-1 flex-shrink-0" style={{ color: 'var(--primary-main)' }} />
                        <span>Shop D1, Near Ambedkar Hospital, Sector 31, Noida</span>
                      </div>
                      <div className="flex items-center gap-2" style={{ color: 'var(--text-muted)' }}>
                        <Phone size={16} style={{ color: 'var(--primary-main)' }} />
                        <a href="tel:+917217648679" className="hover:text-[var(--primary-main)]">+91 7217648679</a>
                      </div>
                      <div className="flex items-center gap-2" style={{ color: 'var(--text-muted)' }}>
                        <Clock size={16} style={{ color: 'var(--primary-main)' }} />
                        <span>Open Daily • Until 10 PM</span>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Link
                        to="/puppies/"
                        className="block w-full text-center py-2.5 rounded-lg font-semibold transition-all hover:-translate-y-0.5"
                        style={{ backgroundColor: 'var(--primary-main)', color: 'white' }}
                      >
                        View Puppies
                      </Link>
                      <Link
                        to="/supplies/"
                        className="block w-full text-center py-2.5 rounded-lg font-semibold transition-all hover:-translate-y-0.5"
                        style={{ backgroundColor: 'var(--primary-light)', color: 'var(--primary-dark)' }}
                      >
                        Pet Supplies
                      </Link>
                      <Link
                        to="/contact/"
                        className="block w-full text-center py-2.5 rounded-lg font-semibold transition-all hover:-translate-y-0.5"
                        style={{ border: '2px solid var(--primary-main)', color: 'var(--primary-main)' }}
                      >
                        Contact Us
                      </Link>
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          </div>
        </section>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <section className="py-12" style={{ backgroundColor: 'white' }}>
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-2xl font-bold mb-6" style={{ color: 'var(--text-main)' }}>
                  Related Articles
                </h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {relatedPosts.map((relPost) => (
                    <Link
                      key={relPost.slug}
                      to={`/blog/${relPost.slug}/`}
                      className="group"
                    >
                      <div
                        className="rounded-xl overflow-hidden transition-all hover:shadow-lg"
                        style={{ backgroundColor: 'var(--neutral-bg)' }}
                      >
                        <div className="relative overflow-hidden" style={{ aspectRatio: '16/10' }}>
                          <img
                            src={relPost.featuredImage}
                            alt={relPost.title}
                            loading="lazy"
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                          />
                        </div>
                        <div className="p-4">
                          <h3 
                            className="font-semibold text-sm line-clamp-2 transition-colors group-hover:text-[var(--primary-main)]"
                            style={{ color: 'var(--text-main)' }}
                          >
                            {relPost.title}
                          </h3>
                          <span 
                            className="inline-flex items-center gap-1 text-xs font-medium mt-2"
                            style={{ color: 'var(--primary-main)' }}
                          >
                            Read More <ArrowRight size={12} />
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}
      </article>

      {/* Blog Content Styles */}
      <style>{`
        .blog-content h2 {
          font-size: 1.5rem;
          font-weight: 700;
          margin-top: 2rem;
          margin-bottom: 1rem;
          color: var(--text-main);
        }
        .blog-content h3 {
          font-size: 1.25rem;
          font-weight: 600;
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
          color: var(--text-main);
        }
        .blog-content h4 {
          font-size: 1.1rem;
          font-weight: 600;
          margin-top: 1.25rem;
          margin-bottom: 0.5rem;
          color: var(--text-main);
        }
        .blog-content p {
          margin-bottom: 1rem;
          line-height: 1.7;
          color: var(--text-muted);
        }
        .blog-content ul, .blog-content ol {
          margin-bottom: 1rem;
          padding-left: 1.5rem;
        }
        .blog-content li {
          margin-bottom: 0.5rem;
          line-height: 1.6;
          color: var(--text-muted);
        }
        .blog-content strong {
          color: var(--text-main);
          font-weight: 600;
        }
        .blog-content table {
          width: 100%;
          border-collapse: collapse;
          margin: 1rem 0;
          font-size: 0.9rem;
        }
        .blog-content th, .blog-content td {
          border: 1px solid var(--neutral-border);
          padding: 0.75rem;
          text-align: left;
        }
        .blog-content th {
          background-color: var(--primary-light);
          font-weight: 600;
        }
        .blog-content em {
          color: var(--text-muted);
          font-style: italic;
        }
        @media (max-width: 640px) {
          .blog-content h2 { font-size: 1.25rem; }
          .blog-content h3 { font-size: 1.1rem; }
          .blog-content h4 { font-size: 1rem; }
        }
      `}</style>
    </>
  );
};

export default BlogArticle;
